import './styles.scoped.css'
import {useState, useEffect, useRef} from 'react'
import {Typography, MenuItem, IconButton, Chip, Menu, Skeleton, Avatar, CircularProgress, Box} from '@mui/material'
import {MoreVert, FilterAlt} from '@mui/icons-material'
import {DataGrid} from '@mui/x-data-grid'
import {DataGridFooter, SkeletonContainer, TableSkeleton, TextSkeleton, AvatarSkeleton, ChipSkeleton} from '../../../../components'
import {scheduleApi} from '../../../../services'
import {format} from 'date-fns'
import {useGlobalStates} from '../../../../contexts/GlobalStatesContext'
import {useNavigate} from 'react-router-dom'
import {useMobile} from '../../../../hooks'
import {getInitials, getColorFromName, parseError, ignoreTimezone, formatTimeDiffShort, formatTime} from '../../../../utils/helpers'
import InfiniteScroll from 'react-infinite-scroll-component'
import {
    MarkAsComplete,
    DeleteVisits,
    CancelVisits,
    RescheduleVisits,
    ClockOut,
    UndoClockIn,
    SendSignatureReminder,
    ChangeCaregiver,
} from '../../../../components'
import {MobileFilter} from '..'
import {useParams} from 'react-router-dom'
import {noResultsFound} from '../../../../assets'

export default ({search, statuses, selectedStatuses, setSelectedStatuses, startDate, endDate, calendarView, setVisitStats}) => {
    const [page, setPage] = useState(0)
    const [pageSize, setPageSize] = useState(10)
    const [rows, setRows] = useState([])
    const [rowCount, setRowCount] = useState(0)
    const [sortBy, setSortBy] = useState(null)
    const [sortOrder, setSortOrder] = useState(null)
    const {setLoading, setMessageBox, branchId} = useGlobalStates()
    const [anchorEl, setAnchorEl] = useState(null)
    const [selectedRow, setSelectedRow] = useState(null)
    const [openMobileFilter, setOpenMobileFilter] = useState(false)
    const [hasMoreScroll, setHasMoreScroll] = useState(true)
    const [scrollStep, setScrollStep] = useState(1)
    const [scrollData, setScrollData] = useState([])
    const [openMarkAsComplete, setOpenMarkAsComplete] = useState(false)
    const [openDeleteVisits, setOpenDeleteVisits] = useState(false)
    const [openCancelVisits, setOpenCancelVisits] = useState(false)
    const [openRescheduleVisits, setOpenRescheduleVisits] = useState(false)
    const [openClockOut, setOpenClockOut] = useState(false)
    const [openUndoClockIn, setOpenUndoClockIn] = useState(false)
    const [openSendSignatureReminder, setOpenSendSignatureReminder] = useState(false)
    const [openChangeCaregiver, setOpenChangeCaregiver] = useState(false)
    const [skeleton, setSkeleton] = useState(true)
    const requestId = useRef(0)
    const scrollRequestId = useRef(0)
    const {caregiverId} = useParams()
    const {mobile} = useMobile()
    const navigate = useNavigate()

    useEffect(() => {
        const timeout = setTimeout(() => {
            if (!calendarView) {
                loadData()
            }
        }, 1000)
        return () => clearTimeout(timeout)
    }, [branchId, page, pageSize, sortBy, sortOrder, search, selectedStatuses, startDate, endDate, mobile, calendarView])

    useEffect(() => {
        const timeout = setTimeout(() => {
            if (mobile && scrollStep == 1) {
                loadScrollData()
            }
        }, 1000)
        return () => clearTimeout(timeout)
    }, [branchId, scrollStep, mobile])

    // silent: a refetch after a mutation keeps the rows on screen behind the global spinner instead of flashing the skeleton.
    const loadData = (silent = false) => {
        if (mobile) {
            if (scrollStep != 1) {
                scrollRequestId.current++
                setSkeleton(true)
            }
            setScrollData([])
            setScrollStep(1)
        } else {
            const id = ++requestId.current
            if (silent) {
                setLoading(true)
            } else {
                setSkeleton(true)
            }
            scheduleApi
                .getCaregiverVisits(branchId, caregiverId, {
                    page: page + 1,
                    page_size: pageSize,
                    search: search,
                    sort_by: sortBy,
                    sort_order: sortOrder,
                    status: selectedStatuses?.length === statuses?.length ? null : selectedStatuses?.join(','),
                    caregiver_ids: caregiverId,
                    date_from: startDate?.format('YYYY-MM-DD'),
                    date_to: endDate?.format('YYYY-MM-DD'),
                })
                .then(({data: {visits, total, status_counts}}) => {
                    if (silent) setLoading(false)
                    if (id !== requestId.current) return
                    setRows(visits)
                    setRowCount(total)
                    setVisitStats(status_counts)
                    setSkeleton(false)
                })
                .catch(error => {
                    if (silent) setLoading(false)
                    if (id !== requestId.current) return
                    setVisitStats(prev => prev || {})
                    const {title, description} = parseError(error)
                    if (description) {
                        setMessageBox({status: 'error', title, description})
                    }
                    setSkeleton(false)
                })
        }
    }

    const loadScrollData = () => {
        const id = ++scrollRequestId.current
        if (branchId) {
            if (scrollStep == 1) {
                setSkeleton(true)
            }
            scheduleApi
                .getCaregiverVisits(branchId, caregiverId, {
                    page: scrollStep,
                    page_size: 10,
                    search: search,
                    sort_by: sortBy,
                    sort_order: sortOrder,
                    status: selectedStatuses?.length === statuses?.length ? null : selectedStatuses?.join(','),
                    caregiver_ids: caregiverId,
                    date_from: startDate?.format('YYYY-MM-DD'),
                    date_to: endDate?.format('YYYY-MM-DD'),
                })
                .then(({data: {visits, status_counts}}) => {
                    if (id !== scrollRequestId.current) return
                    if (visits) {
                        setScrollData(prev => [...prev, ...visits])
                    }
                    setVisitStats(status_counts)
                    setHasMoreScroll(visits?.length > 0)
                    setScrollStep(prev => ++prev)
                    if (scrollStep == 1) {
                        setSkeleton(false)
                    }
                })
                .catch(error => {
                    if (id !== scrollRequestId.current) return
                    setVisitStats(prev => prev || {})
                    const {title, description} = parseError(error)
                    if (description) {
                        setMessageBox({status: 'error', title, description})
                    }
                    if (scrollStep == 1) {
                        setSkeleton(false)
                    }
                })
        }
    }

    const sortChange = data => {
        if (data.length > 0) {
            const {field, sort} = data[0]
            setSortBy(field)
            setSortOrder(sort)
        } else {
            setSortBy(null)
            setSortOrder(null)
        }
    }

    const menuOpen = (e, row) => {
        setAnchorEl(e.currentTarget)
        setSelectedRow(row)
    }

    const menuClose = () => {
        setAnchorEl(null)
    }

    const scheduleDetails = () => {
        menuClose()
        navigate(`/scheduling/${selectedRow?.schedule_id}/visits/${selectedRow?.id}`)
    }

    const updateSchedule = () => {
        menuClose()
        navigate(`/scheduling/schedule-form/${selectedRow?.schedule_id}`)
    }

    const changeCaregiver = () => {
        menuClose()
        setOpenChangeCaregiver(true)
    }

    const markAsComplete = () => {
        menuClose()
        setOpenMarkAsComplete(true)
    }

    const cancelSchedule = () => {
        menuClose()
        setOpenCancelVisits(true)
    }

    const deleteVisit = () => {
        menuClose()
        setOpenDeleteVisits(true)
    }

    const recordClockOut = () => {
        menuClose()
        setOpenClockOut(true)
    }

    const undoClockIn = () => {
        menuClose()
        setOpenUndoClockIn(true)
    }

    const sendSignReminderToCaregiver = () => {
        menuClose()
        setOpenSendSignatureReminder(true)
    }

    const resetCaregiverSignature = () => {
        menuClose()
    }

    const updateScheduleReport = () => {
        menuClose()
    }

    const downloadReport = () => {
        menuClose()
    }

    const claimBillingToDHS = () => {
        menuClose()
    }

    const resetSignature = () => {
        menuClose()
    }

    const reschedule = () => {
        menuClose()
        setOpenRescheduleVisits(true)
    }

    const generatePdfReport = () => {
        menuClose()
    }

    const mobileReset = () => {
        setSelectedStatuses([])
        setSortBy('')
        setSortOrder('')
        setScrollData([])
        setScrollStep(1)
    }

    const onRowClick = row => {
        navigate(`/scheduling/${row?.schedule_id}/visits/${row?.id}`)
    }

    const columns = [
        {
            field: 'date',
            headerName: 'Schedule Time',
            flex: 1,
            renderCell: ({row}) => (
                <div className="schedule">
                    <Typography variant="h9" color="text.primary">
                        {format(new Date(ignoreTimezone(row?.date)), 'MM/dd/yyyy')}
                    </Typography>
                    <Typography variant="body2" color="text.primary">
                        {formatTime(row?.start_time)}-{formatTime(row?.end_time)} ({formatTimeDiffShort(row?.start_time, row?.end_time)})
                    </Typography>
                </div>
            ),
        },
        {
            field: 'service',
            headerName: 'Service',
            flex: 1,
            valueGetter: (value, row) => row?.schedule?.service?.name,
            renderCell: ({row}) => row?.schedule?.service?.name,
        },
        {
            field: 'client',
            headerName: 'Client',
            flex: 1.4,
            valueGetter: (value, row) => {
                return row?.schedule?.client?.first_name + ' ' + row?.schedule?.client?.last_name
            },
            renderCell: ({row}) => (
                <div className="people-card">
                    {row?.schedule?.client?.avatar_url ? (
                        <Avatar src={row?.schedule?.client?.avatar_url} style={{width: 32, height: 32}} />
                    ) : (
                        <Avatar
                            style={{
                                backgroundColor: getColorFromName(row?.schedule?.client?.first_name + ' ' + row?.schedule?.client?.last_name),
                                width: 32,
                                height: 32,
                                fontSize: 16,
                            }}>
                            {getInitials(row?.schedule?.client?.first_name + ' ' + row?.schedule?.client?.last_name)}
                        </Avatar>
                    )}
                    <Typography variant="body2" color="text.primary" className="name">
                        {row?.schedule?.client?.first_name + ' ' + row?.schedule?.client?.last_name}
                    </Typography>
                </div>
            ),
        },
        {
            field: 'total_cost',
            headerName: 'Total Cost',
            flex: 1,
            valueGetter: (value, row) => row?.schedule?.total_cost,
            renderCell: ({row}) => (
                <div className="schedule">
                    <Typography variant="body2" color="text.primary">
                        {row?.total_cost ? '$' + row?.total_cost : ''}
                    </Typography>
                    <Typography variant="body2" color="text.primary">
                        ({row?.total_units} unit)
                    </Typography>
                </div>
            ),
        },
        {
            field: 'status',
            headerName: 'Status',
            flex: 0.8,
            renderCell: ({value}) => {
                if (value === 'scheduled') {
                    return (
                        <Chip
                            label="Scheduled"
                            variant="filled"
                            sx={theme => ({
                                backgroundColor: theme.palette.grey[700],
                                color: theme.palette.secondary.contrastText,
                            })}
                        />
                    )
                } else if (value === 'in_progress') {
                    return <Chip label="In progress" variant="filled" color="warning" />
                } else if (value === 'ended') {
                    return (
                        <Chip
                            label="Ended"
                            variant="filled"
                            sx={theme => ({
                                backgroundColor: '#02CAD1',
                                color: theme.palette.primary.contrastText,
                            })}
                        />
                    )
                } else if (value === 'finished') {
                    return <Chip label="Finished" variant="filled" color="info" />
                } else if (value === 'completed') {
                    return <Chip label="Completed" variant="filled" color="success" />
                } else if (value === 'cancelled') {
                    return <Chip label="Cancelled" variant="filled" color="error" />
                }
                return null
            },
        },
        {
            field: 'action',
            headerName: 'Action',
            flex: 0.4,
            sortable: false,
            renderCell: ({row}) => (
                <>
                    <IconButton
                        onClick={e => {
                            e.stopPropagation()
                            menuOpen(e, row)
                        }}>
                        <MoreVert />
                    </IconButton>
                </>
            ),
        },
    ]

    return (
        <>
            {mobile ? (
                <>
                    <div className="mobile-filter" onClick={() => setOpenMobileFilter(true)}>
                        <FilterAlt color="primary" fontSize="medium" />
                        <Typography variant="buttonMedium" color="primary">
                            Filter
                        </Typography>
                    </div>
                    {skeleton && (
                        <SkeletonContainer>
                            {Array.from({length: 5}).map((_, index) => (
                                <div className="card" key={index}>
                                    <div className="top-card">
                                        <div>
                                            <TextSkeleton width={80} />
                                            <TextSkeleton width={150} />
                                        </div>
                                        <div className="right-card">
                                            <ChipSkeleton width={index % 2 ? 88 : 96} height={32} />
                                            <Skeleton variant="circular" width={24} height={24} sx={{m: 1}} />
                                        </div>
                                    </div>
                                    <div className="people-card">
                                        <AvatarSkeleton size={32} />
                                        <TextSkeleton width={index % 2 ? 84 : 100} />
                                    </div>
                                    <TextSkeleton width={index % 2 ? '45%' : '60%'} />
                                    <TextSkeleton width={90} />
                                </div>
                            ))}
                        </SkeletonContainer>
                    )}
                    {scrollData?.length > 0 && (
                        <InfiniteScroll
                            dataLength={scrollData?.length}
                            next={loadScrollData}
                            hasMore={hasMoreScroll}
                            loader={
                                <Box display="flex" justifyContent="center" my={2}>
                                    <CircularProgress />
                                </Box>
                            }
                            endMessage={
                                <Typography align="center" sx={{mt: 2}}>
                                    All Loaded.
                                </Typography>
                            }>
                            {scrollData.map((row, index) => (
                                <div className="card" key={index} onClick={() => onRowClick(row)}>
                                    <div className="top-card">
                                        <div>
                                            <Typography variant="body2" color="text.primary">
                                                {format(new Date(ignoreTimezone(row?.date)), 'MM/dd/yyyy')}
                                            </Typography>
                                            <Typography variant="body2" color="text.primary">
                                                {formatTime(row?.start_time)}-{formatTime(row?.end_time)} (
                                                {formatTimeDiffShort(row?.start_time, row?.end_time)})
                                            </Typography>
                                        </div>
                                        <div className="right-card">
                                            {row?.status == 'scheduled' && (
                                                <Chip
                                                    label="Scheduled"
                                                    variant="filled"
                                                    sx={theme => ({
                                                        backgroundColor: theme.palette.grey[700],
                                                        color: theme.palette.secondary.contrastText,
                                                    })}
                                                />
                                            )}
                                            {row?.status == 'in_progress' && <Chip label="In progress" variant="filled" color="warning" />}
                                            {row?.status == 'ended' && (
                                                <Chip
                                                    label="Ended"
                                                    variant="filled"
                                                    sx={theme => ({
                                                        backgroundColor: '#02CAD1',
                                                        color: theme.palette.primary.contrastText,
                                                    })}
                                                />
                                            )}
                                            {row?.status == 'finished' && <Chip label="Finished" variant="filled" color="info" />}
                                            {row?.status == 'completed' && <Chip label="Completed" variant="filled" color="success" />}
                                            {row?.status == 'cancelled' && <Chip label="Cancelled" variant="filled" color="error" />}
                                            <IconButton
                                                onClick={e => {
                                                    e.stopPropagation()
                                                    menuOpen(e, row)
                                                }}>
                                                <MoreVert />
                                            </IconButton>
                                        </div>
                                    </div>
                                    <div className="people-card">
                                        {row?.avatar_url ? (
                                            <Avatar src={row?.avatar_url} style={{width: 32, height: 32}} />
                                        ) : (
                                            <Avatar
                                                style={{
                                                    backgroundColor: getColorFromName(row?.first_name + ' ' + row?.last_name),
                                                    width: 32,
                                                    height: 32,
                                                    fontSize: 16,
                                                }}>
                                                {getInitials(row?.first_name + ' ' + row?.last_name)}
                                            </Avatar>
                                        )}
                                        <Typography variant="body2" color="text.primary" className="name">
                                            {row?.schedule?.client?.first_name + ' ' + row?.schedule?.client?.last_name}
                                        </Typography>
                                    </div>
                                    <Typography variant="body2" color="text.primary">
                                        {row?.schedule?.service?.name}
                                    </Typography>
                                    <Typography variant="body2" color="text.primary">
                                        {row?.schedule?.total_cost ? '$' + row?.schedule?.total_cost : ''} (12 unit)
                                    </Typography>
                                </div>
                            ))}
                        </InfiniteScroll>
                    )}
                </>
            ) : (
                <>
                    {skeleton && (
                        <TableSkeleton
                            rows={pageSize}
                            rowHeight={66}
                            columns={[
                                {flex: 1, lines: 2, widths: ['80%', '74%', '78%'], headerWidth: 110},
                                {flex: 1, widths: ['70%', '55%', '80%', '62%']},
                                {flex: 1.4, type: 'avatar', headerWidth: 50},
                                {flex: 1, lines: 2, widths: ['45%', '38%', '50%'], headerWidth: 80},
                                {flex: 0.8, type: 'chip', chipWidth: 92},
                                {flex: 0.4, type: 'actions'},
                            ]}
                        />
                    )}
                    {rows?.length > 0 && (
                        <DataGrid
                            style={{display: skeleton ? 'none' : 'inline'}}
                            rows={rows}
                            columns={columns}
                            paginationModel={{page, pageSize}}
                            onSortModelChange={sortChange}
                            disableRowSelectionOnClick
                            disableColumnMenu
                            getRowHeight={() => 66}
                            onRowClick={e => onRowClick(e.row)}
                            slots={{
                                footer: () => (
                                    <DataGridFooter page={page} setPage={setPage} pageSize={pageSize} setPageSize={setPageSize} rowCount={rowCount} />
                                ),
                            }}
                        />
                    )}
                </>
            )}
            {!skeleton && ((!mobile && rows?.length == 0) || (mobile && scrollData?.length == 0)) && (
                <>
                    <br />
                    <Typography variant="subtitle2" color="text.secondary">
                        Search Results (0)
                    </Typography>
                    <br />
                    <br />
                    <div style={{textAlign: 'center'}}>
                        <img src={noResultsFound} width={mobile ? '200' : '372'} />
                        <br />
                        <br />
                        <div style={{marginBottom: 10}}>
                            <Typography variant={mobile ? 'h7' : 'h5'} color="text.primary">
                                No Results Found
                            </Typography>
                        </div>
                        <Typography variant={mobile ? 'body2' : 'body1'} color="text.primary">
                            No schedule match your search criteria. Try adjusting your filters or criteria to find schedules.
                        </Typography>
                    </div>
                </>
            )}
            <br />
            <br />
            <br />
            <br />
            <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={menuClose}
                anchorOrigin={{vertical: 'bottom', horizontal: 'right'}}
                transformOrigin={{vertical: 'top', horizontal: 'right'}}>
                {selectedRow?.status == 'scheduled' && (
                    <div>
                        <MenuItem onClick={scheduleDetails}>Schedule Details</MenuItem>
                        <MenuItem onClick={updateSchedule}>Update Schedule</MenuItem>
                        <MenuItem onClick={changeCaregiver}>Change Caregiver</MenuItem>
                        <MenuItem onClick={markAsComplete}>Mark as Complete</MenuItem>
                        <MenuItem onClick={cancelSchedule} sx={theme => ({color: theme.palette.error.main})}>
                            Cancel Schedule
                        </MenuItem>
                        <MenuItem onClick={deleteVisit} sx={theme => ({color: theme.palette.error.main})}>
                            Delete Visit
                        </MenuItem>
                    </div>
                )}
                {selectedRow?.status == 'in_progress' && (
                    <div>
                        <MenuItem onClick={scheduleDetails}>Schedule Details</MenuItem>
                        <MenuItem onClick={updateSchedule}>Update Schedule</MenuItem>
                        <MenuItem onClick={recordClockOut}>Record Clock-Out</MenuItem>
                        <MenuItem onClick={markAsComplete}>Mark as Complete</MenuItem>
                        <MenuItem onClick={undoClockIn} sx={theme => ({color: theme.palette.error.main})}>
                            Undo Clock-In
                        </MenuItem>
                        <MenuItem onClick={cancelSchedule} sx={theme => ({color: theme.palette.error.main})}>
                            Cancel Schedule
                        </MenuItem>
                    </div>
                )}
                {selectedRow?.status == 'ended' && (
                    <div>
                        <MenuItem onClick={scheduleDetails}>Schedule Details</MenuItem>
                        <MenuItem onClick={updateSchedule}>Update Schedule</MenuItem>
                        <MenuItem onClick={sendSignReminderToCaregiver}>Send Sign Reminder to Caregiver</MenuItem>
                        <MenuItem onClick={markAsComplete}>Mark as Complete</MenuItem>
                        <MenuItem onClick={cancelSchedule} sx={theme => ({color: theme.palette.error.main})}>
                            Cancel Schedule
                        </MenuItem>
                    </div>
                )}
                {selectedRow?.status == 'finished' && (
                    <div>
                        <MenuItem onClick={scheduleDetails}>Schedule Details</MenuItem>
                        <MenuItem onClick={updateSchedule}>Update Schedule</MenuItem>
                        <MenuItem onClick={markAsComplete}>Mark as Complete</MenuItem>
                        <MenuItem onClick={resetCaregiverSignature}>Reset Caregiver Signature</MenuItem>
                        <MenuItem onClick={cancelSchedule} sx={theme => ({color: theme.palette.error.main})}>
                            Cancel Schedule
                        </MenuItem>
                    </div>
                )}
                {selectedRow?.status == 'completed' && (
                    <div>
                        <MenuItem onClick={scheduleDetails}>Schedule Details</MenuItem>
                        <MenuItem onClick={updateScheduleReport}>Update Schedule Report</MenuItem>
                        <MenuItem onClick={downloadReport}>Download Report</MenuItem>
                        <MenuItem onClick={claimBillingToDHS}>Claim Billing to DHS</MenuItem>
                        <MenuItem onClick={resetSignature}>Reset Signature</MenuItem>
                    </div>
                )}
                {selectedRow?.status == 'cancelled' && (
                    <div>
                        <MenuItem onClick={scheduleDetails}>Schedule Details</MenuItem>
                        <MenuItem onClick={reschedule}>Reschedule</MenuItem>
                    </div>
                )}
            </Menu>
            <MobileFilter
                open={openMobileFilter}
                setOpen={setOpenMobileFilter}
                statuses={statuses}
                selectedStatuses={selectedStatuses}
                setSelectedStatuses={setSelectedStatuses}
                setSortBy={setSortBy}
                setSortOrder={setSortOrder}
                mobileReset={mobileReset}
            />
            <MarkAsComplete open={openMarkAsComplete} setOpen={setOpenMarkAsComplete} loadData={() => loadData(true)} items={[selectedRow]} />
            <DeleteVisits open={openDeleteVisits} setOpen={setOpenDeleteVisits} loadData={() => loadData(true)} items={[selectedRow]} />
            <CancelVisits open={openCancelVisits} setOpen={setOpenCancelVisits} loadData={() => loadData(true)} items={[selectedRow]} />
            <RescheduleVisits open={openRescheduleVisits} setOpen={setOpenRescheduleVisits} loadData={() => loadData(true)} items={[selectedRow]} />
            <ClockOut open={openClockOut} setOpen={setOpenClockOut} loadData={() => loadData(true)} items={[selectedRow]} />
            <UndoClockIn open={openUndoClockIn} setOpen={setOpenUndoClockIn} loadData={() => loadData(true)} items={[selectedRow]} />
            <SendSignatureReminder
                open={openSendSignatureReminder}
                setOpen={setOpenSendSignatureReminder}
                loadData={() => loadData(true)}
                items={[selectedRow]}
            />
            <ChangeCaregiver open={openChangeCaregiver} setOpen={setOpenChangeCaregiver} loadData={() => loadData(true)} items={[selectedRow]} />
        </>
    )
}
